import { Injectable } from '@angular/core';
import { PrivacyDialogComponent } from './popup/privacy-dialog.component';
import { PrivacyDialogModernComponent } from './popup/privacy-dialog-modern.component';
import { NgbModalOpenerService } from '@client/app/services/ngb-modal-opener/ngb-modal-opener.service';
import { terms, activeFeatures } from '@monsido/core/constants/general.constant';
import { ThemeService } from '@client/app/services/theme.service';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslocoService } from '@jsverse/transloco';
import { take } from 'rxjs';
import { UIRouter } from '@uirouter/core';

@Injectable({
    providedIn: 'root',
})
export class TermsService {
    private dialogOpen = false;
    private termsNeeded = false;

    constructor (
        private readonly modalOpenerService: NgbModalOpenerService,
        private readonly themeService: ThemeService,
        private readonly dialogService: DialogService,
        private readonly translocoService: TranslocoService,
        private readonly uiRouter: UIRouter,
    ) {
        // Open the dialog after the route transition succeeds so the theme is correct
        this.uiRouter.transitionService.onSuccess({}, () => {
            if (this.termsNeeded && !this.dialogOpen) {
                this.showTermsDialog();
            }
        });
    }

    checkTermsAcceptance (user: any, isSudo: boolean): void {
        if (!user || this.dialogOpen) {
            return;
        }
        if (
            activeFeatures.accept_terms &&
            !isSudo &&
            (!user.privacy_policy?.accepted_at ||
            user.privacy_policy?.version.toString() !== terms.version.toString())
        ) {
            this.termsNeeded = true;
        }
    }

    private showTermsDialog (): void {
        this.dialogOpen = true;
        if (this.themeService.getCurrentTheme() === 'modern') {
            this.openPrimengDialog();
        } else {
            this.openNgbDialog();
        }
    }

    private openNgbDialog (): void {
        const modalRef = this.modalOpenerService.open({
            component: PrivacyDialogComponent,
            windowClass: 'privacy-modal-open',
            keyboard: false,
            backdrop: 'static',
        });
        modalRef.componentInstance.version = terms.version;
    }

    private openPrimengDialog (): void {
        this.translocoService.selectTranslate('terms.privacy.header').pipe(take(1)).subscribe(header => {
            const ref: DynamicDialogRef = this.dialogService.open(PrivacyDialogModernComponent, {
                header,
                width: '600px',
                modal: true,
                closable: false,
                dismissableMask: false,
                closeOnEscape: false,
                resizable: false,
                draggable: false,
                baseZIndex: 10000,
                data: { version: terms.version },
            });

            // Terms accepted — dialog was closed from within the component
            ref.onClose.subscribe(() => {
                this.termsNeeded = false;
                this.dialogOpen = false;
            });

            // Dialog destroyed without acceptance (e.g. navigation) — allow re-open
            ref.onDestroy.subscribe(() => {
                if (this.termsNeeded) {
                    this.dialogOpen = false;
                }
            });
        });
    }
}
